import type { i18n as I18n } from 'i18next';

const SUPPORTED_LANGUAGES = ['en', 'de', 'fr', 'es', 'pt', 'zh', 'th', 'ar'];
export const LANGUAGE_STORAGE_KEY = 'saved-language';

/**
 * Get language from hash-based routes (/#/de/, /#/es/, etc.)
 */
function getLanguageFromHash(): string | null {
  const hash = window.location.hash;
  // Match patterns like /#/de/ or /#/de/path or /#/de
  const match = hash.match(/^#\/([a-z]{2})(?:\/|$)/);

  if (match && SUPPORTED_LANGUAGES.includes(match[1])) {
    return match[1];
  }

  return null;
}

/**
 * Get language from URL parameters (?lang=de) - kept as fallback
 */
function getLanguageFromURL(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  const langParam = urlParams.get('lang');

  if (langParam && SUPPORTED_LANGUAGES.includes(langParam)) {
    return langParam;
  }

  return null;
}

/**
 * Get stored language preference
 */
function getStoredLanguage(): string | null {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.includes(stored)) {
      return stored;
    }
  } catch (error) {
    console.warn('Failed to read language from localStorage:', error);
  }

  return null;
}

/**
 * Store language preference
 */
export function storeLanguage(language: string): void {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch (error) {
    console.warn('Failed to store language in localStorage:', error);
  }
}

/**
 * Get browser's preferred language
 */
function getBrowserLanguage(): string {
  const browserLang = navigator.language.split('-')[0];
  return SUPPORTED_LANGUAGES.includes(browserLang) ? browserLang : 'en';
}

/**
 * The language the current route asks for. The hash prefix is the single source
 * of truth — `LanguageRoutes` derives the same thing from the same prefix — and
 * its absence means English. Anything that picks a language from somewhere else
 * (stored preference, browser) has to turn it into a prefix first, via
 * `redirectFromBaseURL`, or the two disagree and React aborts the tree with
 * "Maximum update depth exceeded".
 */
export function getRouteLanguage(): string {
  return getLanguageFromHash() || 'en';
}

/**
 * Redirect a bare base URL to the language-prefixed hash route the user prefers.
 * Returns true if a redirect was issued, in which case the caller must stop —
 * the replace() re-enters detection against the new hash.
 *
 * Called once at module load, before React renders, so the router and i18n both
 * start from the post-redirect URL and never have to reconcile afterwards.
 */
export function redirectFromBaseURL(): boolean {
  const currentHash = window.location.hash;

  // Anything else is a real route and carries its own language prefix.
  if (currentHash && currentHash !== '#' && currentHash !== '#/') {
    return false;
  }

  // No route to read a language off, so the preference decides.
  const preferredLanguage = getLanguageFromURL() || getStoredLanguage() || getBrowserLanguage();
  const newHash = preferredLanguage === 'en' ? '#/' : `#/${preferredLanguage}/`;

  if (currentHash === newHash) {
    return false;
  }

  window.location.replace(window.location.origin + window.location.pathname + newHash);
  return true;
}

/**
 * Detect and set the language for the current URL.
 */
export function detectAndSetLanguage(i18nInstance?: I18n): void {
  if (redirectFromBaseURL()) {
    return;
  }

  const detectedLanguage = getRouteLanguage();

  if (i18nInstance && i18nInstance.language !== detectedLanguage) {
    i18nInstance.changeLanguage(detectedLanguage);
  }

  storeLanguage(detectedLanguage);
}
